import { useState, useRef, useCallback } from 'react';
import ReactCrop, { type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Upload, Trash2, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useNotification } from '@/hooks/useNotification';
import { updateUserImage, removeUserImage } from '../../../apiServices/userApi';
import {
  putToStorageProfileImage,
  putToStorageDefault,
} from '../../../apiServices/storageApiService';
import { updateWorkspaceImage, removeWorkspaceImage } from '../../../apiServices/groupApi';
import { updateItemCover, removeCoverImage } from '../../../apiServices/projectApi';
import { pdfToPng } from './SetPictureModalUtilFunctions';
import { getLowerCaseFileExtensionInFile } from '../../../util/UtilFunctions';

// ── Types ────────────────────────────────────────────────────────────

type UseCase = 'profile' | 'workspace' | 'coverImage';

type Status = 'initial' | 'selected' | 'cropped' | 'uploading' | 'removing';

interface SetPictureModalProps {
  useCase: UseCase;
  closeModal: () => void;
  updateIsQuerying: (v: boolean) => void;
  hasImage?: boolean;
  item?: Record<string, any>;
  currentUserInfo?: Record<string, any>;
  updateCurrentUserInfo?: (...args: any[]) => void;
  getWorkspacePeopleList?: () => void;
  updateGroupsList?: (...args: any[]) => void;
  refreshList?: (...args: any[]) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────

const HEADLINE: Record<UseCase, string> = {
  profile: 'Add profile picture',
  workspace: 'Add workspace icon',
  coverImage: 'Add cover image',
};

const MAX_PREVIEW_PX = 320;

/** Downscale an image so the longer side is at most `maxPx`. */
function constrainSize(w: number, h: number, maxPx: number) {
  if (w <= maxPx && h <= maxPx) return { width: w, height: h };
  const ratio = Math.min(maxPx / w, maxPx / h);
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

/** Canvas‑crop the source <img> at `crop`, return a Blob. */
function cropToBlob(image: HTMLImageElement, crop: Crop): Promise<Blob> {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const canvas = document.createElement('canvas');
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width,
    crop.height,
  );
  return new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.92),
  );
}

/** Resize an image element and return a Blob. */
function resizeImageToBlob(img: HTMLImageElement, maxPx: number): Promise<Blob> {
  const { width, height } = constrainSize(img.naturalWidth, img.naturalHeight, maxPx);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
  return new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 1),
  );
}

/** Styled label that looks like a secondary button but wraps a file input. */
function FileInputButton({ onChange, children }: { onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; children: React.ReactNode }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-secondary px-3 h-8 text-sm font-medium text-foreground transition-colors hover:bg-accent">
      {children}
      <input
        type="file"
        className="hidden"
        accept="image/png,image/jpeg,image/jpg,application/pdf"
        onChange={onChange}
      />
    </label>
  );
}

// ── Component ────────────────────────────────────────────────────────

export default function SetPictureModal({
  useCase,
  closeModal,
  updateIsQuerying,
  hasImage,
  item,
  currentUserInfo,
  updateCurrentUserInfo,
  getWorkspacePeopleList,
  updateGroupsList,
  refreshList,
}: SetPictureModalProps) {
  const { notify } = useNotification();
  const imageRef = useRef<HTMLImageElement | null>(null);

  // ── State ────────────────────
  const [status, setStatus] = useState<Status>('initial');
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [croppedUrl, setCroppedUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [crop, setCrop] = useState<Crop>({ unit: 'px', width: 100, height: 100, x: 0, y: 0 });

  // ── File selection ───────────
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    const file = getLowerCaseFileExtensionInFile(raw);
    const mime = file.type.split('/');

    if (mime[1] === 'gif') {
      notify({ type: 'info', message: "GIF files can't be set as cover images" });
      return;
    }

    if (mime[0] === 'image') {
      setIsPdf(false);
      setSourceFile(file);

      // Load image to get dimensions, then optionally downscale for cropper
      const img = new Image();
      const objUrl = URL.createObjectURL(file);
      img.src = objUrl;
      img.onload = async () => {
        if (img.naturalWidth > MAX_PREVIEW_PX || img.naturalHeight > MAX_PREVIEW_PX) {
          const blob = await resizeImageToBlob(img, MAX_PREVIEW_PX);
          setPreviewUrl(URL.createObjectURL(blob));
        } else {
          setPreviewUrl(objUrl);
        }
        const { width, height } = constrainSize(img.naturalWidth, img.naturalHeight, MAX_PREVIEW_PX);
        const side = Math.min(width, height, 100);
        setCrop({ unit: 'px', width: side, height: side, x: 0, y: 0 });
        setStatus('selected');
      };
      return;
    }

    if (mime[1] === 'pdf') {
      setIsPdf(true);
      setSourceFile(file);
      setStatus('selected');
      try {
        const pngFile = await pdfToPng(file);
        setPreviewUrl(URL.createObjectURL(pngFile));
        setCroppedBlob(null); // PDF uses the converted PNG directly
      } catch {
        notify({ type: 'error', message: 'Could not generate PDF thumbnail' });
        setStatus('initial');
      }
      return;
    }

    notify({ type: 'info', message: 'File must be an image or PDF' });
  }, [notify]);

  // ── Crop complete ────────────
  const handleCropComplete = useCallback(async (c: Crop) => {
    if (!imageRef.current || !c.width || !c.height) return;
    const blob = await cropToBlob(imageRef.current, c);
    setCroppedBlob(blob);
    setCroppedUrl(URL.createObjectURL(blob));
    setStatus('cropped');
  }, []);

  // ── Upload to S3 + API ───────
  const uploadToStorage = useCallback(async (blob: Blob, name: string) => {
    const fn = useCase === 'profile' ? putToStorageProfileImage : putToStorageDefault;
    updateIsQuerying(true);
    try {
      return await fn(blob, name);
    } finally {
      updateIsQuerying(false);
    }
  }, [useCase, updateIsQuerying]);

  const handleUpload = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (status !== 'selected' && status !== 'cropped') return;

    const uploadBlob = isPdf ? sourceFile! : croppedBlob;
    if (!uploadBlob) return;

    const fileName = sourceFile?.name || 'image.png';
    setStatus('uploading');

    try {
      const storageResponse = await uploadToStorage(uploadBlob as Blob, fileName);

      if (useCase === 'profile') {
        updateIsQuerying(true);
        await updateUserImage(
          currentUserInfo!.cognitoAttributes.Username,
          storageResponse.key,
          fileName,
        );
        updateIsQuerying(false);
        notify({ type: 'success', message: 'Updated profile picture' });
        updateCurrentUserInfo?.(true, 0);
        getWorkspacePeopleList?.();
      } else if (useCase === 'workspace') {
        const itemId = item!.id;
        await updateWorkspaceImage(itemId, storageResponse.key, fileName);
        notify({ type: 'success', message: 'Updated workspace icon' });
        updateGroupsList?.(itemId, 0);
      } else if (useCase === 'coverImage') {
        const itemId = item!.id;
        updateIsQuerying(true);
        await updateItemCover(itemId, storageResponse);
        updateIsQuerying(false);
        notify({ type: 'success', message: 'Updated cover image' });
        refreshList?.({ type: 'item', id: itemId }, 0, true);
      }

      closeModal();
    } catch (err: any) {
      notify({ type: 'error', message: err?.message || 'Upload failed' });
      setStatus('selected');
    }
  }, [
    status, isPdf, sourceFile, croppedBlob, uploadToStorage,
    useCase, currentUserInfo, item, closeModal, notify,
    updateIsQuerying, updateCurrentUserInfo, getWorkspacePeopleList,
    updateGroupsList, refreshList,
  ]);

  // ── Remove image ─────────────
  const handleRemove = useCallback(async () => {
    setStatus('removing');
    try {
      if (useCase === 'profile') {
        await removeUserImage(currentUserInfo!.cognitoAttributes.Username);
        notify({ type: 'success', message: 'Removed profile picture' });
        updateCurrentUserInfo?.();
      } else if (useCase === 'workspace') {
        await removeWorkspaceImage(item!.id);
        notify({ type: 'success', message: 'Removed workspace icon' });
        updateGroupsList?.();
      } else if (useCase === 'coverImage') {
        await removeCoverImage(item!.id);
        notify({ type: 'success', message: 'Removed cover image' });
        refreshList?.();
      }
      closeModal();
    } catch {
      notify({ type: 'error', message: `Could not remove ${useCase === 'profile' ? 'profile picture' : useCase === 'workspace' ? 'workspace icon' : 'cover image'}` });
      setStatus('initial');
    }
  }, [useCase, currentUserInfo, item, closeModal, notify, updateCurrentUserInfo, updateGroupsList, refreshList]);

  // ── Existing image preview ───
  const existingImageUrl = (() => {
    if (useCase === 'profile') return currentUserInfo?.image?.S;
    if (useCase === 'workspace') return item?.image?.S;
    if (useCase === 'coverImage' && item?.previewImages?.length) return item.previewImages[0].S;
    return null;
  })();

  // ── Render ───────────────────
  return (
    <div className="space-y-4">
      {/* ── Preview area ── */}
      <div className="flex items-center justify-center min-h-56 rounded-lg bg-secondary p-4">
        {status === 'initial' && (
          existingImageUrl ? (
            <img
              src={existingImageUrl}
              alt="current"
              className={`max-h-40 max-w-40 object-cover ${useCase === 'profile' ? 'rounded-full' : 'rounded-md'}`}
            />
          ) : (
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border p-8 text-muted-foreground transition hover:border-primary hover:text-primary">
              <Upload className="h-8 w-8" />
              <span className="text-sm font-medium">Select an image or PDF</span>
              <input
                type="file"
                className="hidden"
                accept="image/png,image/jpeg,image/jpg,application/pdf"
                onChange={handleFileSelect}
              />
            </label>
          )
        )}

        {(status === 'selected' || status === 'cropped') && !isPdf && previewUrl && (
          <ReactCrop
            crop={crop}
            onChange={setCrop}
            onComplete={handleCropComplete}
            aspect={1}
            circularCrop={useCase === 'profile'}
            keepSelection
          >
            <img
              ref={imageRef}
              src={previewUrl}
              alt="crop preview"
              onLoad={(e) => { imageRef.current = e.currentTarget; }}
            />
          </ReactCrop>
        )}

        {(status === 'selected' || status === 'cropped') && isPdf && previewUrl && (
          <img src={previewUrl} alt="PDF preview" className="max-h-64 rounded" />
        )}

        {(status === 'selected' || status === 'cropped') && !previewUrl && (
          <Spinner />
        )}

        {status === 'uploading' && (
          <div className="flex flex-col items-center gap-2">
            {croppedUrl && (
              <img
                src={croppedUrl}
                alt="uploading"
                className={`max-h-40 max-w-40 object-cover ${useCase === 'profile' ? 'rounded-full' : 'rounded-md'}`}
              />
            )}
            <Spinner />
            <span className="text-sm text-muted-foreground">Uploading…</span>
          </div>
        )}

        {status === 'removing' && (
          <div className="flex flex-col items-center gap-2">
            <Spinner />
            <span className="text-sm text-muted-foreground">Removing…</span>
          </div>
        )}
      </div>

      {/* ── Action buttons ── */}
      <div className="flex items-center justify-end gap-3">
        {(status === 'initial' && hasImage) && (
          <>
            <FileInputButton onChange={handleFileSelect}>
              <ImageIcon className="mr-1.5 h-4 w-4" />
              Choose new
            </FileInputButton>
            <Button variant="destructive" size="sm" onClick={handleRemove}>
              <Trash2 className="mr-1.5 h-4 w-4" />
              Remove
            </Button>
          </>
        )}

        {(status === 'selected' || status === 'cropped') && (
          <>
            <FileInputButton onChange={handleFileSelect}>
              Choose another
            </FileInputButton>
            <Button
              size="sm"
              onClick={handleUpload}
              disabled={!isPdf && !croppedBlob}
            >
              <Upload className="mr-1.5 h-4 w-4" />
              Upload
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
